import efafcLogo from '../assets/efafc.png';
import { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle2, MailCheck, RefreshCw } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendEmailVerification, reload, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';

export default function AuthPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' ou 'register'
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ email: '', password: '', pseudo: '' });
  const [successMsg, setSuccessMsg] = useState('');
  const [pendingValidation, setPendingValidation] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [checkingValidation, setCheckingValidation] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.emailVerified) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Cooldown pour éviter le spam du bouton de renvoi
  function startCooldown() {
    setResendCooldown(10);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setSuccess(false);
    setPendingValidation(false);
    try {
      if (mode === 'login') {
        const { user } = await signInWithEmailAndPassword(auth, form.email, form.password);
        if (!user.emailVerified) {
          setError("Adresse email non vérifiée. Vérifie ta boîte mail (et tes spams).");
          setPendingValidation(true);
          return;
        }
        setSuccess(true);
        setSuccessMsg('Connexion réussie !');
        setTimeout(() => {
          setSuccess(false);
          navigate('/');
        }, 2000);
      } else {
        // Création de compte
        const { user } = await createUserWithEmailAndPassword(auth, form.email, form.password);
        await updateProfile(user, { displayName: form.pseudo });
        await setDoc(doc(db, "users", user.uid), {
          pseudo: form.pseudo,
          email: form.email,
          role: "user",
          badges: [],
          avatar: "https://ui-avatars.com/api/?name=" + encodeURIComponent(form.pseudo) + "&background=001B56&color=fff",
          bio: "",
          points: 0,
          createdAt: new Date(),
          lastLogin: new Date(),
          preferences: {
            notifications: true,
            theme: "light",
            language: "fr"
          }
        });
        await sendEmailVerification(user);
        setPendingValidation(true);
        setSuccessMsg(`Un email de confirmation a été envoyé à ${form.email}. Vérifie aussi tes spams.`);
        startCooldown();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Renvoi de l'email de confirmation
  const handleResend = async () => {
    setError('');
    setSuccessMsg('');
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setSuccessMsg(`Email de confirmation renvoyé à ${auth.currentUser.email}. Vérifie aussi tes spams.`);
        startCooldown();
      } else {
        setError("Utilisateur non connecté. Merci de vous reconnecter.");
      }
    } catch (err) {
      setError("Erreur lors de l'envoi de l'email : " + err.message);
    }
  };

  // Vérification de la validation de l'email
  const handleCheckValidation = async () => {
    setCheckingValidation(true);
    setError('');
    setSuccessMsg('');
    try {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        if (auth.currentUser.emailVerified) {
          setSuccess(true);
          setPendingValidation(false);
          setSuccessMsg('Compte validé ! Tu peux maintenant te connecter.');
          setTimeout(() => setSuccess(false), 2500);
        } else {
          setError("Ton email n'est pas encore validé. Pense à vérifier tes spams.");
        }
      } else {
        setError("Utilisateur non connecté. Merci de vous reconnecter.");
      }
    } catch (err) {
      setError("Erreur lors de la vérification : " + err.message);
    }
    setCheckingValidation(false);
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#001B56] relative">
      {/* Bouton retour */}
      <button
        onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-1 text-white hover:text-[#BDFF00] font-bold text-base z-50 bg-transparent p-0 border-none shadow-none"
        style={{background:'none', boxShadow:'none'}}
        type="button"
      >
        <span style={{fontSize:'1.5rem', lineHeight:'1'}}>&larr;</span>
        Retour
      </button>
      <div className="absolute top-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <Link to="/">
          <img src={efafcLogo} alt="EFAFC Logo" className="h-20 mb-2 drop-shadow-xl hover:scale-105 transition cursor-pointer" />
        </Link>
      </div>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl px-8 py-10 flex flex-col items-center mt-32">
        <div className="flex w-full justify-center gap-4 mb-6">
          <button
            className={`px-4 py-2 rounded-lg font-bold text-lg transition border-b-4 ${mode === 'login' ? 'border-[#BDFF00] text-[#001B56]' : 'border-transparent text-gray-400 hover:text-[#001B56]'}`}
            onClick={() => { setMode('login'); setError(''); setPendingValidation(false); setSuccess(false); }}
          >
            Connexion
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-bold text-lg transition border-b-4 ${mode === 'register' ? 'border-[#BDFF00] text-[#001B56]' : 'border-transparent text-gray-400 hover:text-[#001B56]'}`}
            onClick={() => { setMode('register'); setError(''); setPendingValidation(false); setSuccess(false); }}
          >
            Créer un compte
          </button>
        </div>
        {/* Validation email après inscription ou tentative de connexion sans email vérifié */}
        {pendingValidation && (
          <div className="flex flex-col items-center mb-4 animate-fade-in w-full">
            <MailCheck size={48} className="text-[#BDFF00] mb-2 animate-pop" />
            <span className="text-[#001B56] font-bold text-lg text-center mb-2">{successMsg || `Un email de confirmation a été envoyé à ${form.email}.`}</span>
            <button
              onClick={handleResend}
              className="w-full bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold py-2 rounded-lg text-base shadow transition mb-2 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0 ? `Renvoyer l'email (${resendCooldown}s)` : "Renvoyer l'email de confirmation"}
            </button>
            <button
              onClick={handleCheckValidation}
              className="w-full bg-[#BDFF00] hover:bg-[#FFE400] text-[#001B56] font-bold py-2 rounded-lg text-base shadow transition flex items-center justify-center gap-2"
              disabled={checkingValidation}
            >
              <RefreshCw size={18} className={checkingValidation ? 'animate-spin' : ''} />
              J'ai validé mon email
            </button>
            {error && <div className="text-red-600 text-sm font-semibold text-center mt-2">{error}</div>}
          </div>
        )}
        {/* Validation visuelle finale */}
        {success && !pendingValidation && (
          <div className="flex flex-col items-center mb-4 animate-fade-in">
            <CheckCircle2 size={48} className="text-[#BDFF00] mb-2 animate-pop" />
            <span className="text-[#001B56] font-bold text-lg text-center">{successMsg}</span>
          </div>
        )}
        {/* Formulaire principal (affiché si pas en attente de validation) */}
        {!success && !pendingValidation && (
          <form className="w-full flex flex-col gap-5" onSubmit={handleSubmit}>
            {mode === 'register' && (
              <div>
                <label htmlFor="pseudo" className="block text-[#001B56] font-semibold mb-1">Pseudo</label>
                <input
                  type="text"
                  id="pseudo"
                  name="pseudo"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
                  placeholder="Ton pseudo"
                  required
                  value={form.pseudo}
                  onChange={handleChange}
                />
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-[#001B56] font-semibold mb-1">Adresse Email</label>
              <input
                type="email"
                id="email"
                name="email"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
                placeholder="exemple@email.com"
                required
                value={form.email}
                onChange={handleChange}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="block text-[#001B56] font-semibold mb-1">Mot de passe</label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#BDFF00] focus:ring-2 focus:ring-[#BDFF00] outline-none text-[#001B56] bg-gray-50"
                placeholder="••••••••"
                required
                value={form.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute right-3 top-9 text-[#001B56] hover:text-[#BDFF00]"
                tabIndex={-1}
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <div className="flex justify-end">
              <a href="#" className="text-[#001B56] hover:text-[#BDFF00] text-sm font-semibold">Mot de passe oublié ?</a>
            </div>
            {error && <div className="text-red-600 text-sm font-semibold text-center -mt-2">{error}</div>}
            <button
              type="submit"
              className="w-full bg-[#BDFF00] hover:bg-[#FFE400] text-[#001B56] font-bold py-3 rounded-lg text-lg shadow transition"
            >
              {mode === 'login' ? 'Connexion' : 'Créer mon compte'}
            </button>
          </form>
        )}
      </div>
      <footer className="w-full text-center text-white text-xs mt-10 opacity-60">
        © {new Date().getFullYear()} Entente Feignies Aulnoye FC. Tous droits réservés.
      </footer>
      <style>{`
        .animate-fade-in { animation: fade-in 0.5s; }
        @keyframes fade-in { from { opacity: 0; transform: scale(0.95);} to { opacity: 1; transform: scale(1);} }
        .animate-pop { animation: pop 0.5s cubic-bezier(.4,2,.6,1); }
        @keyframes pop { 0% { transform: scale(0.7); opacity: 0.5; } 60% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
